import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import TopBar from "@/components/TopBar";
import Marquee from "@/components/Marquee";
import HeroCarousel from "@/components/HeroCarousel";
import CampaignCard from "@/components/CampaignCard";
import CampaignDetail from "@/components/CampaignDetail";
import Leaderboard from "@/components/Leaderboard";
import VerificationSection from "@/components/VerificationSection";
import CommunityBanner from "@/components/CommunityBanner";
import { api } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Logo } from "@/components/brand";

const PLATFORMS = [["", "All"], ["instagram", "Reels"], ["youtube", "Shorts"]];
const SORTS = [["newest", "Newest"], ["highest_payout", "Highest payout"], ["ending_soon", "Ending soon"]];

export default function Discover() {
  const [hero, setHero] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [count, setCount] = useState(0);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [platform, setPlatform] = useState("");
  const [sort, setSort] = useState("newest");
  const [openSlug, setOpenSlug] = useState(null);
  const { slug } = useParams();
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    const [h, s] = await Promise.all([
      api.get("/campaigns/hero").catch(() => ({ data: { slides: [] } })),
      api.get("/settings/public").catch(() => ({ data: {} })),
    ]);
    setHero(h.data.slides || []);
    setSettings(s.data || {});
    const { data } = await api.get("/campaigns", { params: { search, platform, sort } });
    setCampaigns(data.campaigns || []);
    setCount(data.count || 0);
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [search, platform, sort]);
  useEffect(() => { if (slug) setOpenSlug(slug); }, [slug]);

  const openCampaign = (c) => { setOpenSlug(c.slug); window.history.replaceState(null, "", `/campaign/${c.slug}`); };
  const closeCampaign = () => { setOpenSlug(null); window.history.replaceState(null, "", "/"); };

  return (
    <div style={{ minHeight: "100vh" }}>
      <TopBar brandBg={settings.brand_bg_url} />
      <div className="container-x" style={{ marginTop: 24 }}>
        {loading && !hero.length ? <div className="hero-box"><Skeleton className="w-full h-full" style={{ borderRadius: 16 }} /></div> : <HeroCarousel slides={hero} onOpen={openCampaign} />}
      </div>
      <div className="container-x" style={{ marginTop: 24 }}>
        <Marquee items={settings.marquee_items} />
      </div>

      <div className="container-x" style={{ marginTop: 35 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18, flexWrap: "wrap", gap: 12 }}>
          <h2 style={{ fontSize: 18, margin: 0, lineHeight: 1 }}>All campaigns <span style={{ color: "var(--muted)", fontSize: 14 }}>· {count}</span></h2>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <div style={{ position: "relative" }}>
              <Search size={14} style={{ position: "absolute", left: 10, top: 11, color: "var(--muted)" }} />
              <input className="input-dark" placeholder="Search" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="search-input" style={{ paddingLeft: 30, width: 180, height: 44, paddingTop: 0, paddingBottom: 0 }} />
            </div>
            <select className="input-dark" value={sort} onChange={(e) => setSort(e.target.value)} data-testid="sort-select" style={{ width: "auto", height: 44 }}>
              {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
          {PLATFORMS.map(([v, l]) => (
            <button key={v} onClick={() => setPlatform(v)} data-testid={`filter-${v || "all"}`} className="focus-ring" style={{ padding: "6px 14px", borderRadius: 999, fontSize: 13, cursor: "pointer", border: "1px solid " + (platform === v ? "var(--accent)" : "var(--border)"), background: platform === v ? "rgba(42,79,214,.15)" : "transparent", color: platform === v ? "var(--accent-light)" : "var(--sub)" }}>{l}</button>
          ))}
        </div>

        {loading ? (
          <div className="campaign-grid">{[...Array(6)].map((_, i) => <Skeleton key={i} style={{ height: 280, borderRadius: 12 }} />)}</div>
        ) : campaigns.length === 0 ? (
          <div style={{ textAlign: "center", padding: 60, color: "var(--muted)" }}>No campaigns found.</div>
        ) : (
          <div className="campaign-grid" data-testid="campaign-grid">
            {campaigns.map((c) => <CampaignCard key={c.id} campaign={c} onOpen={openCampaign} />)}
          </div>
        )}
      </div>

      <div className="container-x" style={{ marginTop: 64 }}>
        <Leaderboard />
      </div>
      <div className="container-x" style={{ marginTop: 64 }}>
        <VerificationSection />
      </div>

      <CommunityBanner settings={settings} />

      <footer className="container-x" style={{ marginTop: 64, padding: "28px 0 40px", borderTop: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center", color: "var(--muted)", fontSize: 13 }}>
        <Logo size={24} brandBg={settings.brand_bg_url} />
        <span>© {new Date().getFullYear()} Clipup</span>
      </footer>

      <CampaignDetail slug={openSlug} open={!!openSlug} onClose={closeCampaign} />
    </div>
  );
}
